import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  Plus,
  Clock,
  Target,
  Zap,
  Dumbbell
} from 'lucide-react'

const CreatePlanModal = ({ isOpen, onClose, onCreatePlan }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 60,
    difficulty: 'intermediate'
  })
  const [errors, setErrors] = useState({})

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validation
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }
    if (formData.duration < 10 || formData.duration > 180) {
      newErrors.duration = 'Duration must be between 10 and 180 minutes'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    onCreatePlan(formData)
    handleClose()
  }

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      duration: 60,
      difficulty: 'intermediate'
    })
    setErrors({})
    onClose()
  }

  const planTemplates = [
    {
      name: 'Full Body Strength',
      description: 'Complete full-body workout targeting all major muscle groups',
      duration: 60,
      difficulty: 'intermediate',
      icon: '💪'
    },
    {
      name: 'HIIT Cardio',
      description: 'High-intensity interval training for maximum calorie burn',
      duration: 30,
      difficulty: 'advanced',
      icon: '🔥'
    },
    {
      name: 'Beginner Routine',
      description: 'Perfect starting point for fitness beginners',
      duration: 45,
      difficulty: 'beginner',
      icon: '🌟'
    },
    {
      name: 'Upper Body Focus',
      description: 'Targeted upper body strength and muscle building',
      duration: 50,
      difficulty: 'intermediate',
      icon: '🏋️'
    }
  ]

  const handleTemplateSelect = (template) => {
    setFormData({
      name: template.name,
      description: template.description,
      duration: template.duration,
      difficulty: template.difficulty
    })
    setErrors({})
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100'
      case 'intermediate': return 'text-yellow-600 bg-yellow-100'
      case 'advanced': return 'text-red-600 bg-red-100'
      default: return 'text-slate-600 bg-slate-100'
    }
  }

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return <Target className="h-4 w-4" />
      case 'intermediate': return <Dumbbell className="h-4 w-4" />
      case 'advanced': return <Zap className="h-4 w-4" />
      default: return <Target className="h-4 w-4" />
    }
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Exercise Plan
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Form Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Plan Details</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Plan Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter plan name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description *
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the purpose and goals of this workout plan"
                  rows={3}
                  className={errors.description ? 'border-red-500' : ''}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Estimated Duration (minutes) *
                </label>
                <Input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                  placeholder="60"
                  min="10"
                  max="180"
                  className={errors.duration ? 'border-red-500' : ''}
                />
                {errors.duration && (
                  <p className="text-red-500 text-sm mt-1">{errors.duration}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Difficulty Level
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['beginner', 'intermediate', 'advanced'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, difficulty: level }))}
                      className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                        formData.difficulty === level
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1 mb-1">
                        {getDifficultyIcon(level)}
                      </div>
                      <div className="capitalize">{level}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" variant="fitness" className="flex-1">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Plan
                </Button>
              </div>
            </form>
          </div>

          {/* Templates Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Start Templates</h3>
            <p className="text-sm text-slate-600 mb-4">
              Choose a template to get started quickly, or create from scratch.
            </p>
            
            <div className="space-y-3">
              {planTemplates.map((template, index) => (
                <Card 
                  key={index}
                  className="cursor-pointer transition-shadow"
                  onClick={() => handleTemplateSelect(template)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">{template.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 mb-1">
                          {template.name}
                        </h4>
                        <p className="text-sm text-slate-600 mb-2">
                          {template.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <div className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {template.duration} min
                            </div>
                            <span className={`px-2 py-1 rounded-full font-medium ${getDifficultyColor(template.difficulty)}`}>
                              {template.difficulty}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">💡 Pro Tip</h4>
              <p className="text-sm text-blue-800">
                Start with a template and customize it to fit your client's specific needs. 
                You can always modify exercises, sets, reps, and add custom sections later.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CreatePlanModal